import { DefaultLabField } from "@/services/backend/samples/components/sample-base-data-default-lab-field";
import { Kind } from "@/services/backend/samples/types/kind";
import { DefaultDeviceField } from "@/services/backend/samples/components/sample-base-data-default-device-field";
import { UUID } from "@bitory-gmbh/nidavellir";
import {
  PatchRequest,
  updateSamplesAirMicrobialBaseDataCache,
  useSamplesAirMicrobialBaseDataPatchMutation,
  useSamplesAirMicrobialBaseDataShowQuery,
} from "@/services/backend/samples/api/air-microbial-base-data-api";
import { AirMicrobialBaseData } from "@/services/backend/samples/types/sample-base-data";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import {
  NumberInputField,
  TextInputField,
} from "@/shared/components/field/input-field";
import { CheckboxField } from "@/services/backend/samples/components/sample-base-data-checkbox-field";

/**
 * The patch carrying every editable value. Sending all of them — not just
 * the changed one — keeps a quick edit of a second field from replacing
 * the pending patch of the first one before it is sent.
 */
function patchOf(data: AirMicrobialBaseData): PatchRequest {
  return {
    id: data.id,
    label: data.label,
    unit: data.unit,
    sampleUnit: data.sampleUnit,
    samplingVolume: data.samplingVolume,
    dg18: data.dg18,
    labId: data.labId,
    deviceId: data.deviceId,
  };
}

export function AirMicrobialBaseDataFields({ id }: { id: UUID }) {
  const {
    data,
    error: dataError,
    isLoading: dataLoading,
  } = useSamplesAirMicrobialBaseDataShowQuery({ id });

  const request: PatchRequest = { id };
  const [set, { error }] = useOptimisticDebouncedMutation(
    request,
    updateSamplesAirMicrobialBaseDataCache,
    useSamplesAirMicrobialBaseDataPatchMutation(),
  );

  if (dataError) {
    return <RTKQueryErrorAlert error={dataError} />;
  }

  if (dataLoading || !data) {
    return <CardContentSkeleton />;
  }

  const edit = (change: Partial<AirMicrobialBaseData>) => {
    const next = { ...data, ...change };
    set(patchOf(next), next);
  };

  return (
    <div className="grid gap-4">
      {/* The label is unique per company, so a rename can be rejected
          with a conflict rather than a field error. */}
      <RTKQueryErrorAlert error={error} />
      <TextInputField
        label={t("Bezeichnung")}
        description={t(
          "Unterscheidet die Basisdaten eines Unternehmens voneinander.",
        )}
        text={data.label}
        onTextChange={(label) => edit({ label })}
        errors={inputValidationErrorMessages(error, "label")}
      />
      <TextInputField
        label={t("Einheit (Satz)")}
        text={data.unit}
        onTextChange={(unit) => edit({ unit })}
        errors={inputValidationErrorMessages(error, "unit")}
      />
      <TextInputField
        label={t("Einheit (Probe)")}
        text={data.sampleUnit}
        onTextChange={(sampleUnit) => edit({ sampleUnit })}
        errors={inputValidationErrorMessages(error, "sampleUnit")}
      />
      <NumberInputField
        label={t("Probenvolumen")}
        number={data.samplingVolume}
        onNumberChange={(samplingVolume) =>
          edit({ samplingVolume: Math.max(0, Math.trunc(samplingVolume)) })
        }
        min={0}
        step={1}
        errors={inputValidationErrorMessages(error, "samplingVolume")}
      />
      <CheckboxField
        label={t("DG18-Probe nehmen")}
        description={t(
          "Neben den Caso- und Malz-Proben wird eine DG18-Probe genommen.",
        )}
        checked={data.dg18}
        onCheckedChange={(dg18) => edit({ dg18 })}
        errors={inputValidationErrorMessages(error, "dg18")}
      />
      <DefaultLabField
        kind={Kind.AirMicrobial}
        labId={data.labId}
        onLabIdChange={(labId) => edit({ labId })}
        errors={inputValidationErrorMessages(error, "labId")}
      />
      <DefaultDeviceField
        deviceId={data.deviceId}
        onDeviceIdChange={(deviceId) => edit({ deviceId })}
        errors={inputValidationErrorMessages(error, "deviceId")}
      />
    </div>
  );
}
